import { ErrorMessage } from "@hookform/error-message";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";
import { camelCase, has, isEmpty } from "lodash";

import UserContent from "~/components/UserContent";
import ErrorBoundary from "~/components/ErrorBoundary";

import { Error } from "~/components/FormElements/Input";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { getErrorMessage, getLegend } from "~/utilities/formHelpers";

export const OptionsWrapper = styled.div`
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
`;

export const RadioLabel = styled.label`
    display: flex;
    gap: 0.5rem;
    align-items: center;
    font-size: 1.8rem;
    color: ${({ theme }) => theme.palette.black};
    cursor: ${({ disabled }) => (disabled ? `not-allowed` : "pointer")};

    &:focus,
    &:focus-within {
        outline: 3px solid ${({ theme }) => theme.palette.focusBlue};
        outline-offset: 5px;
    }
`;

export const Circle = styled.span`
    position: relative;
    border: 1px solid
        ${({ theme, error }) =>
            error ? theme.palette.red : theme.palette.grayTwo};
    width: 20px;
    height: 20px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &::after {
        content: "";
        display: none;
        position: absolute;
        width: 10px;
        height: 10px;
        border-radius: 100%;
        background-color: ${({ theme }) => theme.palette.white};
    }
`;

export const Input = styled.input`
    opacity: 0;
    height: 0;
    width: 0;
    position: absolute;

    &:checked + span:after {
        display: block;
    }

    &:checked ~ span {
        background-color: ${({ theme }) => theme.palette.grayTwo};
    }
`;

const Radio = ({
    label,
    name,
    register,
    labelPlacement,
    options,
    errors = null,
    required = false,
    disabled = false,
    errorMessage = "",
}) => {
    if (isEmpty(options)) {
        return null;
    }

    return (
        <ErrorBoundary>
            <fieldset
                aria-labelledby={`${camelCase(label)}FieldLabel`}
                aria-describedby={
                    has(errors, name) ? `${name}-field-error` : null
                }
            >
                {getLegend(labelPlacement, required, label)}

                <OptionsWrapper>
                    {options.map(({ text, value }, index) => (
                        <RadioLabel
                            key={index}
                            htmlFor={`${name}_${index}`}
                            disabled={disabled}
                            error={errors[`${name}_${index}`]}
                        >
                            <Input
                                type="radio"
                                id={`${name}_${index}`}
                                name={name}
                                disabled={disabled}
                                value={value}
                                {...register(name, {
                                    required: required
                                        ? getErrorMessage(errorMessage, label)
                                        : false,
                                })}
                            />

                            <Circle error={has(errors, name)} />

                            <UserContent>
                                {required &&
                                labelPlacement === "hidden_label" ? (
                                    <span aria-hidden="true">*</span>
                                ) : (
                                    ""
                                )}
                                {sanitizeAndParseHTML(text)}
                            </UserContent>
                        </RadioLabel>
                    ))}
                </OptionsWrapper>

                <ErrorMessage
                    {...{ errors, name }}
                    render={({ message }) => (
                        <Error id={`${name}-field-error`}>{message}</Error>
                    )}
                />
            </fieldset>
        </ErrorBoundary>
    );
};

Radio.propTypes = {
    errors: PropTypes.shape({}),
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.string,
};

export default Radio;
